import React, { useState, useCallback, useEffect } from "react";
import FloatingCard from "../../../ui/FloatingCard";
import ScoringModeToggle from "../../live/components/ScoringModeToggle";
import InlinePlayerGrid from "../common/InlinePlayerGrid";
import PermissionBadge from "../common/PermissionBadge";
import BasicScoreBox from "../common/BasicScoreBox";
import {
  getTableTennisFormat,
  checkRallyGameWinner,
  getTableTennisServer,
  getTableTennisServesLeft,
  getTableTennisGameFirstServer,
  isTableTennisDoubles,
} from "../../../../utils/sportRules";

// ─── localStorage helpers ────────────────────────────────────────────────────
const TT_KEY = (id) => `table_tennis_games_${id}`;
const saveTT = (id, data) => { try { localStorage.setItem(TT_KEY(id), JSON.stringify(data)); } catch {} };
const loadTT = (id) => { try { const r = localStorage.getItem(TT_KEY(id)); return r ? JSON.parse(r) : null; } catch { return null; } };

/**
 * Read table tennis match format (ITTF defaults: best of 5 games to 11,
 * win by 2, no cap). Points/margin come from match_config.scoring_rules and
 * the number of games from match_config.game_rules.
 */
const getTTConfig = (match) => {
  const { totalGames, ptsToWin, winMargin, gamesToWin } = getTableTennisFormat(match);
  return { totalGames, ptsToWin, winMargin, gamesToWin };
};

/** Winner of a table tennis game: first to 11 with a 2-point lead, no cap. */
const checkTTGameWon = (p1, p2, ptsToWin = 11, winMargin = 2) =>
  checkRallyGameWinner(p1, p2, ptsToWin, winMargin, null);

export { getTTConfig, checkTTGameWon };

const TableTennisScoreboard = ({
  match,
  currentScore,
  isReadOnlyMode,
  isMatchActive,
  onScoreIncrease,
  onScoreDecrease,
  onResetScore,
  onResetSetScore,
  onCompleteMatch,
  onStartMatch,
  isTimeoutActive,
  onToggleTimeout,
  matchDuration,
  scoringMode,
  onScoringModeChange,
  advancedSupported = false,
  announcerMuted,
  onAnnouncerToggleMute,
  onAnnouncerRepeat,
  p1Players = [],
  p2Players = [],
  onPlayerSelect,
  onGameComplete,
  externalGameState,
}) => {
  const matchId = match?.id;
  const { totalGames, ptsToWin, winMargin, gamesToWin } = getTTConfig(match);
  const isSingleGame = totalGames === 1;
  const isDoubles = isTableTennisDoubles(match);

  // ── Game state (persisted per match) ─────────────────────────────────────
  const saved = loadTT(matchId);
  const [gamesWon, setGamesWon] = useState(() => saved?.gamesWon ?? { p1: 0, p2: 0 });
  const [gameScores, setGameScores] = useState(() => saved?.gameScores ?? []);
  const [currentGame, setCurrentGame] = useState(() => Math.min(saved?.currentGame ?? 1, totalGames));
  const [matchWon, setMatchWon] = useState(() => saved?.matchWon ?? false);
  // Who served first in game 1 — the first server alternates every game.
  const [matchFirstServer, setMatchFirstServer] = useState(() => saved?.matchFirstServer ?? "p1");

  // Sync game state pushed by the scorer (viewers)
  useEffect(() => {
    if (!externalGameState || externalGameState.currentGame === undefined) return;
    setGamesWon(externalGameState.gamesWon ?? { p1: 0, p2: 0 });
    setGameScores(externalGameState.gameScores ?? []);
    setCurrentGame(externalGameState.currentGame);
    setMatchWon(externalGameState.matchWon ?? false);
    if (externalGameState.matchFirstServer) setMatchFirstServer(externalGameState.matchFirstServer);
  }, [externalGameState]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const p1Score = currentScore?.participant1 || 0;
  const p2Score = currentScore?.participant2 || 0;
  const gameWinner = checkTTGameWon(p1Score, p2Score, ptsToWin, winMargin);
  const isDeuce = p1Score >= ptsToWin - 1 && p2Score >= ptsToWin - 1 && !gameWinner;

  const gameFirstServer = getTableTennisGameFirstServer(currentGame, matchFirstServer);
  const server = getTableTennisServer(p1Score, p2Score, gameFirstServer, ptsToWin);
  const servesLeft = getTableTennisServesLeft(p1Score, p2Score, ptsToWin);

  const dit = (match?.matchConfig || match?.match_config)?.drop_in_teams;
  const p1Name = dit ? (dit.team_a?.name || "Player A") : (match?.participant_1_name || match?.participant1Name || "Player 1");
  const p2Name = dit ? (dit.team_b?.name || "Player B") : (match?.participant_2_name || match?.participant2Name || "Player 2");

  const buildCompletionData = useCallback((gw, gs) => {
    const winnerKey = gw.p1 >= gamesToWin ? "p1" : gw.p2 >= gamesToWin ? "p2" : null;
    if (!winnerKey) return null;
    const p1Id   = match?.participant_1_id   || match?.participant1Id;
    const p2Id   = match?.participant_2_id   || match?.participant2Id;
    const p1Type = match?.participant_1_type || match?.participant1Type || "user";
    const p2Type = match?.participant_2_type || match?.participant2Type || "user";
    const base = {
      cancelled: false,
      winner: { id: winnerKey === "p1" ? p1Id : p2Id, type: winnerKey === "p1" ? p1Type : p2Type },
      winnerName: winnerKey === "p1" ? p1Name : p2Name,
    };
    // Single game: the result is the point score (e.g. 11–7)
    if (totalGames === 1 && gs[0]) {
      return { ...base, participant1Score: gs[0].p1, participant2Score: gs[0].p2 };
    }
    return { ...base, participant1Score: gw.p1, participant2Score: gw.p2, gameScores: gs };
  }, [gamesToWin, totalGames, match, p1Name, p2Name]);

  // ── Game completion ───────────────────────────────────────────────────────
  // The header's Complete: a game that is won but not yet completed counts, so
  // pressing it right after the deciding point records the games (2–0), not
  // that game's points (21–0).
  const completionForHeader = () => {
    const countCurrent = !matchWon && (gameWinner === "p1" || gameWinner === "p2");
    if (!countCurrent) return buildCompletionData(gamesWon, gameScores);
    return buildCompletionData(
      { p1: gamesWon.p1 + (gameWinner === "p1" ? 1 : 0), p2: gamesWon.p2 + (gameWinner === "p2" ? 1 : 0) },
      [...gameScores, { p1: p1Score, p2: p2Score }]
    );
  };

  const handleCompleteGame = useCallback(() => {
    if (matchWon) {
      const data = buildCompletionData(gamesWon, gameScores);
      if (data && onCompleteMatch) onCompleteMatch(data);
      return;
    }
    if (!gameWinner) return;

    const newGamesWon = {
      p1: gamesWon.p1 + (gameWinner === "p1" ? 1 : 0),
      p2: gamesWon.p2 + (gameWinner === "p2" ? 1 : 0),
    };
    const newGameScores = [...gameScores, { p1: p1Score, p2: p2Score }];
    const newMatchWon = newGamesWon.p1 >= gamesToWin || newGamesWon.p2 >= gamesToWin;
    const newCurrentGame = newMatchWon ? currentGame : Math.min(currentGame + 1, totalGames);

    setGamesWon(newGamesWon);
    setGameScores(newGameScores);
    setCurrentGame(newCurrentGame);
    setMatchWon(newMatchWon);
    const newState = {
      gamesWon: newGamesWon,
      gameScores: newGameScores,
      currentGame: newCurrentGame,
      matchWon: newMatchWon,
      matchFirstServer,
    };
    saveTT(matchId, newState);
    if (onGameComplete) onGameComplete(newState);

    if (!newMatchWon) {
      const resetFn = onResetSetScore || onResetScore;
      if (resetFn) resetFn({ eventType: "game_start", description: `Game ${newCurrentGame} started`, period: newCurrentGame });
    } else if (onCompleteMatch) {
      onCompleteMatch(buildCompletionData(newGamesWon, newGameScores));
    }
  }, [matchWon, gameWinner, gamesWon, gameScores, p1Score, p2Score, gamesToWin, currentGame, totalGames,
      matchFirstServer, matchId, onGameComplete, onResetSetScore, onResetScore, onCompleteMatch, buildCompletionData]);

  const toggleFirstServer = () => {
    const next = matchFirstServer === "p1" ? "p2" : "p1";
    setMatchFirstServer(next);
    saveTT(matchId, { gamesWon, gameScores, currentGame, matchWon, matchFirstServer: next });
  };
  const canChooseServer = !isReadOnlyMode && currentGame === 1 && p1Score === 0 && p2Score === 0 && gameScores.length === 0;

  // ── Controls ──────────────────────────────────────────────────────────────
  const isAdvancedInline = scoringMode === "advanced" && advancedSupported && !!onPlayerSelect;
  const addBlocked = !isMatchActive || !!gameWinner || matchWon;
  const p1SubBlocked = !isMatchActive || matchWon || p1Score <= 0;
  const p2SubBlocked = !isMatchActive || matchWon || p2Score <= 0;

  const renderAdvanced = (participant, score, players) => {
    if (isReadOnlyMode || !isAdvancedInline) return null;
    const subBlocked = !isMatchActive || matchWon || score <= 0;
    return (
      <div className="vb-adv-inline">
        <InlinePlayerGrid participant={participant} players={players} isDisabled={addBlocked} onPlayerSelect={onPlayerSelect} />
        <button className="vb-deduct-btn" onClick={() => onScoreDecrease(participant, 1)} disabled={subBlocked}>−1</button>
      </div>
    );
  };

  const serveBadge = (side) => (isMatchActive && !gameWinner && !matchWon && server === side) ? (
    <span className="participant-type" data-testid={`tt-serve-${side}`} title="Serving">
      🏓 Serving · {servesLeft} serve{servesLeft === 1 ? "" : "s"} left
    </span>
  ) : null;

  const rulesLine = `First to ${ptsToWin} · Win by ${winMargin} · No cap${isSingleGame ? "" : ` · ${gamesToWin} games to win`}${isDoubles ? " · Doubles" : ""}`;

  return (
    <FloatingCard variant="gradient" size="large" className="score-display-websocket delay-3" glow={isMatchActive}>
      <div className="score-header-websocket">
        <div className="score-header-top">
          <h3>
            🏓 Table Tennis
            {!isSingleGame && (
              <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: "0.88em" }}>
                {" — "}Game {currentGame} of {totalGames}
              </span>
            )}
          </h3>
          <div className="score-header-top-right">
            {!isReadOnlyMode && onScoringModeChange && (
              <ScoringModeToggle
                mode={scoringMode || "basic"}
                onChange={onScoringModeChange}
                sportSupported={advancedSupported}
                disabled={!isMatchActive}
              />
            )}
            <PermissionBadge isReadOnlyMode={isReadOnlyMode} />
          </div>
        </div>

        {isMatchActive && onAnnouncerToggleMute && (
          <div className="score-announcer-bar">
            <button
              className={`announcer-btn${announcerMuted ? " muted" : ""}`}
              onClick={onAnnouncerToggleMute}
              aria-label={announcerMuted ? "Unmute score announcer" : "Mute score announcer"}
            >
              {announcerMuted ? "🔇" : "🔊"}
            </button>
            <span className="announcer-label">Score Announcer</span>
            <button className="announcer-btn" onClick={onAnnouncerRepeat} aria-label="Repeat last score announcement">🔁</button>
          </div>
        )}

        {!isReadOnlyMode && (
          <div className="live-match-quick-controls">
            {match?.status === "scheduled" && onStartMatch && (
              <button className="live-ctrl-btn live-ctrl-start" onClick={onStartMatch}>🎬 Start Match</button>
            )}
            {isMatchActive && (
              <>
                <button className={`live-ctrl-btn live-ctrl-timeout${isTimeoutActive ? " active" : ""}`} onClick={onToggleTimeout}>
                  {isTimeoutActive ? "▶️ Resume" : "⏸️ Timeout"}
                </button>
                <button className="live-ctrl-btn live-ctrl-reset" onClick={onResetScore} disabled={matchWon || !!gameWinner}>
                  🔄 Reset Game
                </button>
                <button
                  className="live-ctrl-btn live-ctrl-complete"
                  onClick={() => {
                    if (!onCompleteMatch) return;
                    const data = completionForHeader();
                    if (data) onCompleteMatch(data); else onCompleteMatch();
                  }}
                >
                  🏁 Complete
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Game history */}
      {!isSingleGame && (
        <div style={{ display: "flex", gap: "0.35rem", justifyContent: "center", alignItems: "center", marginBottom: "0.45rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.72rem", color: "#64748b" }}>Games:</span>
          {Array.from({ length: totalGames }, (_, i) => {
            const gs = gameScores[i];
            const label = gs ? `G${i + 1} ${gs.p1}–${gs.p2}` : `G${i + 1}`;
            const live = !gs && i + 1 === currentGame && !matchWon;
            return (
              <span
                key={i}
                style={{
                  padding: "0.18rem 0.45rem", borderRadius: "0.3rem", fontSize: "0.68rem",
                  background: live ? "rgba(34,197,94,0.2)" : "rgba(0,0,0,0.2)",
                  color: live ? "#22c55e" : gs ? "#94a3b8" : "#475569",
                  border: `1px solid ${live ? "rgba(34,197,94,0.5)" : "rgba(71,85,105,0.3)"}`,
                }}
              >
                {label}
              </span>
            );
          })}
          <span style={{ marginLeft: "0.4rem", fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
            {gamesWon.p1}–{gamesWon.p2}
          </span>
        </div>
      )}

      <div style={{ textAlign: "center", fontSize: "0.68rem", color: "#64748b", marginBottom: "0.65rem" }}>{rulesLine}</div>

      {canChooseServer && isMatchActive && (
        <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
          <button className="live-ctrl-btn" onClick={toggleFirstServer}>
            🏓 First server: {matchFirstServer === "p1" ? p1Name : p2Name} (switch)
          </button>
        </div>
      )}

      {gameWinner && isMatchActive && !isReadOnlyMode && (
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap", padding: "0.75rem 1rem", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.4)", borderRadius: "0.5rem", marginBottom: "0.75rem" }}>
          <span style={{ color: "#22c55e", fontWeight: 700 }}>
            🏆 {gameWinner === "p1" ? p1Name : p2Name} won{isSingleGame ? " the game" : ` Game ${currentGame}`}! ({p1Score}–{p2Score})
          </span>
          {!isSingleGame && !matchWon && (gamesWon[gameWinner] + 1) < gamesToWin ? (
            <button onClick={handleCompleteGame} style={{ padding: "0.4rem 1.1rem", background: "rgba(34,197,94,0.25)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              ✅ Complete Game {currentGame} — Start Game {currentGame + 1}
            </button>
          ) : (
            <button onClick={handleCompleteGame} style={{ padding: "0.4rem 1.1rem", background: "rgba(34,197,94,0.25)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              🏆 Complete Match
            </button>
          )}
        </div>
      )}

      {isDeuce && (
        <div style={{ textAlign: "center", fontSize: "0.75rem", color: "#f59e0b", marginBottom: "0.5rem", fontWeight: 600 }}>
          ⚡ Deuce — win by 2 · service alternates every point
        </div>
      )}

      <div className="participants-websocket">
        <div className="vb-participant-col">
          <div className="vb-team-name">
            {p1Name}
            {!isSingleGame && <span className="participant-type">Games Won: {gamesWon.p1}</span>}
            {serveBadge("p1")}
          </div>
          {!isReadOnlyMode && !isAdvancedInline ? (
            <BasicScoreBox
              participantClassName="participant-1"
              score={p1Score}
              disabled={addBlocked}
              subDisabled={p1SubBlocked}
              onIncrease={() => onScoreIncrease("participant1", 1)}
              onDecrease={() => onScoreDecrease("participant1", 1)}
              addLabel={`Add 1 point for ${p1Name}`}
              subLabel={`Subtract 1 point for ${p1Name}`}
            />
          ) : (
            <div className="participant-websocket participant-1">
              <div className="participant-score-large">{p1Score}</div>
              {renderAdvanced("participant1", p1Score, p1Players)}
            </div>
          )}
        </div>

        <div className="vs-divider-websocket">
          <div className="vs-circle-websocket">{isSingleGame ? "VS" : `G${currentGame}`}</div>
          <div className="vs-duration" style={{ fontSize: "0.85rem", color: "#64748b", whiteSpace: "nowrap", textAlign: "center" }}>
            {isMatchActive ? "In Progress" : match?.status === "scheduled" ? "Scheduled" : (match?.status?.replace(/_/g, " ") ?? "—")}
          </div>
          {matchDuration && (
            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: isTimeoutActive ? "#f59e0b" : "#64748b", textAlign: "center", marginTop: "0.2rem" }}>
              ⏱ {matchDuration}
            </div>
          )}
        </div>

        <div className="vb-participant-col">
          <div className="vb-team-name">
            {p2Name}
            {!isSingleGame && <span className="participant-type">Games Won: {gamesWon.p2}</span>}
            {serveBadge("p2")}
          </div>
          {!isReadOnlyMode && !isAdvancedInline ? (
            <BasicScoreBox
              participantClassName="participant-2"
              score={p2Score}
              disabled={addBlocked}
              subDisabled={p2SubBlocked}
              onIncrease={() => onScoreIncrease("participant2", 1)}
              onDecrease={() => onScoreDecrease("participant2", 1)}
              addLabel={`Add 1 point for ${p2Name}`}
              subLabel={`Subtract 1 point for ${p2Name}`}
            />
          ) : (
            <div className="participant-websocket participant-2">
              <div className="participant-score-large">{p2Score}</div>
              {renderAdvanced("participant2", p2Score, p2Players)}
            </div>
          )}
        </div>
      </div>
    </FloatingCard>
  );
};

export default TableTennisScoreboard;
